import { getCurrentUser, getAuthHeader, API_BASE_URL, get } from './base';

export const createSurvey = async ({ workspaceId = null }) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/createSurvey`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        title: "Untitled Survey",
        questions: [],
        styles: {
          fontFamily: "Inter",
          submitButtonColor: "#4F46E5",
          submitButtonTextColor: "#FFFFFF",
          backgroundColor: "#FFFFFF",
          useCustomSubmitText: false,
          customSubmitText: "",
        },
        workspaceId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating survey:", error);
    throw error;
  }
};

export const createOnboardingSurvey = async ({
  title,
  questions,
  styles,
  welcomeScreen,
  endingScreens,
}) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/createSurvey`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        title: title || "Untitled Survey",
        questions: questions || [],
        styles: styles || {
          fontFamily: "Inter",
          submitButtonColor: "#4F46E5",
          backgroundColor: "#FFFFFF",
          useCustomSubmitText: false,
          customSubmitText: "",
        },
        welcomeScreen: welcomeScreen || null,
        endingScreens: endingScreens || [],
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating survey:", error);
    throw error;
  }
};

export const saveSurvey = async (surveyData) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const surveyId = surveyData.surveyId || surveyData._id;
    if (!surveyId) {
      throw new Error("No valid surveyId found in survey data");
    }

    // Ensure complete styles object - keep ALL existing properties
    const styles = {
      // Spread ALL existing styles first
      ...surveyData.styles,
      // Only set defaults for required fields if they don't exist
      fontFamily: surveyData.styles?.fontFamily ?? "Inter",
      submitButtonColor: surveyData.styles?.submitButtonColor ?? "#4F46E5",
      submitButtonTextColor: surveyData.styles?.submitButtonTextColor ?? "#FFFFFF",
      backgroundColor: surveyData.styles?.backgroundColor ?? "#FFFFFF",
      questionTextColor: surveyData.styles?.questionTextColor ?? "#000000",
      optionTextColor: surveyData.styles?.optionTextColor ?? "#4B5563",
      cornerRadius: surveyData.styles?.cornerRadius ?? "medium",
      titleSize: surveyData.styles?.titleSize ?? "2xl",
      questionSize: surveyData.styles?.questionSize ?? "xl",
      nextButtonText: surveyData.styles?.nextButtonText ?? "Next",
      submitButtonText: surveyData.styles?.submitButtonText ?? "Submit",
      useCustomSubmitText: surveyData.styles?.useCustomSubmitText ?? false,
      customSubmitText: surveyData.styles?.customSubmitText ?? "",
      useNumberLabels: surveyData.styles?.useNumberLabels ?? false,
      showNextButton: surveyData.styles?.showNextButton ?? false,
      rtlEnabled: surveyData.styles?.rtlEnabled ?? false,
      optionLabelStyle: surveyData.styles?.optionLabelStyle ?? "letters",
      language: surveyData.styles?.language,
      titleFont: surveyData.styles?.titleFont,
      bodyFont: surveyData.styles?.bodyFont,
      backgroundGradient: surveyData.styles?.backgroundGradient,
      backgroundEffect: surveyData.styles?.backgroundEffect,
      surfaceStyle: surveyData.styles?.surfaceStyle,
      surfaceBg: surveyData.styles?.surfaceBg,
      surfaceBlur: surveyData.styles?.surfaceBlur,
      surfaceBorder: surveyData.styles?.surfaceBorder,
      accentGlow: surveyData.styles?.accentGlow,
      motionPreset: surveyData.styles?.motionPreset,
      soundTheme: surveyData.styles?.soundTheme,
      cursorEffect: surveyData.styles?.cursorEffect,
      interactionStyle: surveyData.styles?.interactionStyle,
      welcomeTransition: surveyData.styles?.welcomeTransition,
      buttonColor: surveyData.styles?.buttonColor,
      buttonTextColor: surveyData.styles?.buttonTextColor,
      // Theme name for display in sidebar
      themeName: surveyData.styles?.themeName ?? null,
      logo: {
        url: surveyData.styles?.logo?.url ?? "",
        size: surveyData.styles?.logo?.size ?? "md",
        alignment: surveyData.styles?.logo?.alignment ?? "left",
      },
    };
    
    const requestData = {
      surveyId,
      title: surveyData.title,
      rules: surveyData?.rules,
      questions: surveyData.questions,
      styles,
    };
    // Only send ending/welcome screens when the caller actually provided them.
    // The server replaces these wholesale, so coercing a missing value to []/null
    // here would let a partial or stale save silently wipe screens from the DB.
    if (surveyData.endingScreens !== undefined) {
      requestData.endingScreens = surveyData.endingScreens;
    }
    if (surveyData.welcomeScreen !== undefined) {
      requestData.welcomeScreen = surveyData.welcomeScreen;
    }
    
    const authHeaders = await getAuthHeader();
    const body = JSON.stringify(requestData);
    
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/api/saveSurvey`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body,
      });
    } catch (fetchErr) {
      console.warn("Direct fetch failed, retrying through proxy:", fetchErr.message);
      response = await fetch(`/api/saveSurvey`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body,
      });
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error saving survey:", error);
    throw error;
  }
};

export const getAllSurveys = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return [];
    }

    const apiEndpoint = "/api/survey/all-surveys";
    const url = `${API_BASE_URL}${apiEndpoint}`;

    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const options = {
      method: "GET",
      headers,
      mode: "cors",
      credentials:
        process.env.NODE_ENV === "development" ? "include" : "same-origin",
    };

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response error:", {
        status: response.status,
        text: errorText,
        headers: Object.fromEntries(response.headers.entries()),
      });
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching surveys:", error);
    throw error;
  }
};

export const getSurvey = async (surveyId) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const response = await fetch(`${API_BASE_URL}/api/surveys/${surveyId}`, {
      method: "GET",
      headers: {
        ...(await getAuthHeader()),
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching survey:", error);
    throw error;
  }
};

export const updateSurveySettings = async (surveyId, settings) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const response = await fetch(`${API_BASE_URL}/api/surveys/${surveyId}/settings`, {
      method: "PATCH",
      headers: {
        ...(await getAuthHeader()),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error updating survey settings:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating survey settings:", error);
    throw error;
  }
};

export const getSurveyWithResponses = async (surveyId, filters = {}) => {
  try {
    const { searchText, dateRange, activeFilters, paginate, page, limit, statusFilter } =
      filters;

    // Build query params
    const params = new URLSearchParams();

    // Add search text if present
    if (searchText) {
      params.append("searchText", searchText);
    }

    // Add date range if present
    if (dateRange?.start) {
      params.append("startDate", dateRange.start.format("YYYY-MM-DD"));
    }
    if (dateRange?.end) {
      params.append("endDate", dateRange.end.format("YYYY-MM-DD"));
    }

    // Add any active filters
    if (activeFilters?.length > 0) {
      params.append("filters", JSON.stringify(activeFilters));
    }

    // Add status filter if present
    if (statusFilter) {
      params.append("statusFilter", statusFilter);
    }

    // Always send paginate explicitly: the server defaults to paginate=true
    // with limit=50, so omitting the param when paginate is false would
    // silently cap the results at 50.
    params.append("paginate", paginate ? "true" : "false");

    if (page) {
      params.append("page", page);
    }

    if (limit) {
      params.append("limit", limit);
    }

    // Build URL with query params
    const queryString = params.toString();
    return get(
      `/api/surveys/${surveyId}/with-responses${
        queryString ? `?${queryString}` : ""
      }`
    );
  } catch (error) {
    console.error("Error in getSurveyWithResponses:", error);
    throw error;
  }
};
export const deleteSurvey = async (surveyId) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const response = await fetch(`${API_BASE_URL}/api/surveys/${surveyId}`, {
      method: "DELETE",
      headers: {
        ...(await getAuthHeader()),
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Delete response error:", {
        status: response.status,
        text: errorText,
      });
      throw new Error(`Failed to delete survey: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error("Error in deleteSurvey:", error);
    throw error;
  }
};

// Aliases for consistency with existing code
export const fetchSurvey = async (surveyId, { preview } = {}) => {
  try {
    const qs = preview ? '?preview=1' : '';
    const response = await fetch(
      `${API_BASE_URL}/api/surveys/${surveyId}/public${qs}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch survey");
    }
    const data = await response.json();
    
    // Ensure styles object exists and backgroundImage is never null
    if (data.styles) {
      data.styles.backgroundImage = data.styles.backgroundImage || "";
    } else {
      data.styles = {
        ...data.styles,
        backgroundImage: "",
      };
    }

    return data;
  } catch (error) {
    console.error("Error in fetchSurvey:", error);
    throw error;
  }
};

export const duplicateSurvey = async (surveyId, newTitle = null) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const requestBody = newTitle ? { title: newTitle } : {};
    
    const response = await fetch(
      `${API_BASE_URL}/api/duplicateSurvey/${surveyId}`,
      {
        method: "POST",
        headers: {
          ...(await getAuthHeader()),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Duplicate response error:", {
        status: response.status,
        text: errorText,
      });
      throw new Error(`Failed to duplicate survey: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in duplicateSurvey:", error);
    throw error;
  }
};

export const recordSurveyView = async (surveyId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/surveys/${surveyId}/view`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error recording survey view:", error);
    return { success: false };
  }
};

export const recordSurveyStart = async (surveyId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/surveys/${surveyId}/start`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error recording survey start:", error);
    return { success: false };
  }
};

export const renameSurvey = async ({ surveyId, newTitle }) => {
  try {
    if (!surveyId || !newTitle) {
      throw new Error("No valid surveyId or Title found in survey data");
    }

    // Use getAuthHeader so the active organization (X-Organization-Id) is sent.
    // Without it, renaming an org-owned survey (e.g. one duplicated inside a
    // shared workspace, whose userId is the org owner) resolves to a personal
    // access query and returns 404 for non-owner members.
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/survey/rename`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ surveyId, newTitle }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error renaming survey:", error);
    throw error;
  }
};

export const changeOpenStatus = async ({ surveyId, newStatus }) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    if (!surveyId) {
      throw new Error("No valid surveyId found in survey data");
    }

    const response = await fetch(
      `${API_BASE_URL}/api/survey/change-open-status`,
      {
        method: "PUT",
        headers: {
          ...(await getAuthHeader()),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ surveyId, newStatus }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error renaming survey:", error);
    throw error;
  }
};

export const getSurveyAnalytics = async (surveyId) => {
  try {
    return get(`/api/survey/analytics/${surveyId}`);
  } catch (error) {
    console.error("Error in getSurveyWithResponses:", error);
    throw error;
  }
};

export const getSurveyResponsePerDateAnalytics = async (surveyId) => {
  try {
    return get(`/api/survey/analytics/response-per-date/${surveyId}`);
  } catch (error) {
    console.error("Error in getSurveyResponsePerDateAnalytics:", error);
    throw error;
  }
};
